import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import scholzLogo from "@/assets/scholz-logo.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-effect bg-background/95 border-b border-border/50">
      <div className="container mx-auto px-4 sm:px-8 py-4 sm:py-6">
        <nav className="flex items-center justify-between" role="navigation" aria-label="Hauptnavigation">
          <div className="flex items-center space-x-4">
            <Link 
              to="/" 
              className="cursor-pointer"
              onClick={() => {
                // Scroll to top when clicking logo
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              aria-label="Zur Startseite"
            >
              <img src={scholzLogo} alt="Scholz Interim Logo - Executive Search für Hotellerie und Gastronomie" className="h-6 sm:h-8 opacity-90 hover:opacity-100 transition-opacity" />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-12">
            <Link 
              to="/" 
              className="text-muted-foreground hover:text-foreground transition-smooth text-sm font-medium"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Home
            </Link>
            {/* Dropdown Menu for Über uns */}
            <div className="relative group">
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const aboutSection = document.querySelector('#about');
                    aboutSection?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="flex items-center text-muted-foreground hover:text-foreground transition-smooth text-sm font-medium"
                aria-label="Über uns - Untermenü verfügbar"
              >
                Über uns
                <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-background/95 backdrop-blur-lg border border-border/50 rounded-lg shadow-floating opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2">
                  <button 
                    onClick={() => {
                      navigate('/');
                      setTimeout(() => {
                        const aboutSection = document.querySelector('#about');
                        aboutSection?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Über uns
                  </button>
                  <button 
                    onClick={() => {
                      navigate('/');
                      setTimeout(() => {
                        const referencesSection = document.querySelector('#references-section');
                        referencesSection?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Referenzen
                  </button>
                </div>
              </div>
            </div>
            
            {/* Dropdown Menu for Für Unternehmen */}
            <div className="relative group">
                <Link 
                  to="/unternehmen" 
                  className="flex items-center text-muted-foreground hover:text-foreground transition-smooth text-sm font-medium"
                  aria-label="Für Unternehmen - Untermenü verfügbar"
                >
                  Für Unternehmen
                  <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
              </Link>
              <div className="absolute top-full left-0 mt-2 w-48 bg-background/95 backdrop-blur-lg border border-border/50 rounded-lg shadow-floating opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2">
                  <Link 
                    to="/unternehmen" 
                    className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Übersicht
                  </Link>
                  <Link 
                    to="/managementleistungen" 
                    className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Management
                  </Link>
                  <button 
                    onClick={() => {
                      navigate('/', { state: { scrollToExpertise: true } });
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Headhunting
                  </button>
                </div>
              </div>
            </div>
            
            <button 
              className="text-muted-foreground hover:text-foreground transition-smooth text-sm font-medium"
              onClick={() => {
                navigate('/bewerbende');
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
            >
              Für Bewerbende
            </button>
            <Link to="/verpachtung" className="text-muted-foreground hover:text-foreground transition-smooth text-sm font-medium">
              Verpachtung
            </Link>
            <Button 
              variant="default" 
              size="sm" 
              className="font-medium"
              onClick={() => {
                navigate('/bewerbende', { state: { scrollToFilters: true } });
              }}
            >
              Offene Stellen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border/50 shadow-floating">
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className="text-muted-foreground hover:text-foreground transition-smooth text-base font-medium py-2 border-b border-border/30"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Home
                </Link>
                <div className="relative">
                  <button 
                    className="flex items-center justify-between w-full text-muted-foreground hover:text-foreground transition-smooth text-base font-medium py-2 border-b border-border/30"
                    onClick={(e) => {
                      e.preventDefault();
                      const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                      if (submenu) {
                        submenu.classList.toggle('hidden');
                      }
                    }}
                  >
                    Über uns
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div className="hidden pl-4 py-2 space-y-2">
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/');
                        setTimeout(() => {
                          const aboutSection = document.querySelector('#about');
                          aboutSection?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="block w-full text-left text-muted-foreground hover:text-foreground transition-smooth text-sm py-1"
                    >
                      Über uns
                    </button>
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/');
                        setTimeout(() => {
                          const referencesSection = document.querySelector('#references-section');
                          referencesSection?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="block w-full text-left text-muted-foreground hover:text-foreground transition-smooth text-sm py-1"
                    >
                      Referenzen
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <button 
                    className="flex items-center justify-between w-full text-muted-foreground hover:text-foreground transition-smooth text-base font-medium py-2 border-b border-border/30"
                    onClick={(e) => {
                      e.preventDefault();
                      const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                      if (submenu) {
                        submenu.classList.toggle('hidden');
                      }
                    }}
                  >
                    Für Unternehmen
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div className="hidden pl-4 py-2 space-y-2">
                    <Link 
                      to="/unternehmen" 
                      className="block text-muted-foreground hover:text-foreground transition-smooth text-sm py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Übersicht
                    </Link>
                    <Link 
                      to="/managementleistungen" 
                      className="block text-muted-foreground hover:text-foreground transition-smooth text-sm py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Management
                    </Link>
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/', { state: { scrollToExpertise: true } });
                      }}
                      className="block w-full text-left text-muted-foreground hover:text-foreground transition-smooth text-sm py-1"
                    >
                      Headhunting
                    </button>
                  </div>
                </div>
                <button 
                  className="text-left text-muted-foreground hover:text-foreground transition-smooth text-base font-medium py-2 border-b border-border/30"
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/bewerbende');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                >
                  Für Bewerbende
                </button>
                <Link 
                  to="/verpachtung" 
                  className="text-muted-foreground hover:text-foreground transition-smooth text-base font-medium py-2 border-b border-border/30"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Verpachtung
                </Link>
                <div className="pt-4">
                  <Button 
                    variant="default" 
                    size="lg" 
                    className="w-full font-medium"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/bewerbende', { state: { scrollToFilters: true } });
                    }}
                  >
                    Offene Stellen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};